<template>
    <div>
        <LoadingIntro v-if="loading" />

        <div v-if="scholarship && !loading" class="single-scholarship grid-wrapper">
            <div class="full-width-grid intro-background margin--l-xl">
                <div class="grid-wrapper">
                    <div class="intro__content  single-scholarship__intro">
                        <div class="single-scholarship__intro__left">
                            <button @click="goBackToScholarships" class="secondary-button download__button red-color margin--2xs">
                                <span v-html="ico_arrow_left_16"/>
                                <p v-if="lastPath === 'scholarships'">{{ printString('back_to_search_results') }}</p>
                                <p v-else-if="lastPath === 'my-dashboard'">{{ printString('back_to_my_dashboard') }}</p>
                            </button>

                            <h1 class="margin--xs black-primary" v-if="scholarship.scholarship_name">
                                {{ scholarship.scholarship_name }}
                            </h1>

                            <p v-if="scholarship.organization_name" class="step--2">
                                {{ scholarship.organization_name }}
                            </p>
                        </div>
            
                        <div class="single-scholarship__intro__buttons">
                            <Menu :theme="tooltipTheme" :container="menuContainer" :disabled="is_logged_in">
                                <button 
                                    class="general-button border-grey" 
                                    :class="{ 'saved': isSaved, 'disabled': isSavedListFull && !isSaved }" 
                                    @click.stop="handleSaveClick"
                                    @keydown.enter.prevent="handleSaveClick"
                                    
                                    :disabled="isSaveFetching"
                                >
                                    <span class="vh">{{ isSaved ? 'Remove from favorites' : 'Add to favorites' }}</span>
                                    <span v-if="isSaved" v-html="ico_close_16"/>
                                    <span v-else v-html="ico_favorite_16"/>
                                    <span v-if="isSaveFetching">{{ printString('loading') }}</span>
                                    <span v-if="isSaved && !isSaveFetching">
                                        {{ printString('remove') }}
                                    </span>
                                    <span v-else>
                                        {{ printString('save') }}
                                    </span>
                                </button>

                                <template #popper v-if="!is_logged_in">
                                    <div>
                                        <a class="secondary-button red-color" :href="loginUrl" @click.stop>
                                            {{ printString('sign_in') }}
                                        </a>
                                        {{ printString('to_add_to_list') }}
                                    </div>
                                </template>
                            </Menu>
                        </div>
                    </div>
                </div>

                <div class="intro-gradient">
                    <div class="intro-gradient__red"></div>
                    <div class="intro-gradient__yellow"></div>
                    <div class="intro-gradient__white"></div>
                    <div class="intro-gradient__white"></div>
                </div>
            </div>

            <div class="single-scholarship__content__holder margin--xl-2xl">
                <div class="single-scholarship__sidebar">
                    <div class="scholarship-block__label__holder" v-if="labelText || scholarship.featured">
                        <div class="program-block__label" v-if="scholarship.featured">
                            <p>{{ printString('featured') }}</p>
                        </div>
                        <div v-if="labelText" class="scholarship-block__label" :class="scholarship_status">
                            <p v-if="scholarship_status === 'opening'">
                                {{ printString('opening_soon') }}
                            </p>
                            <p v-else-if="scholarship_status === 'closing'">
                                {{ printString('closing_soon') }}
                            </p>
                            <p v-else-if="scholarship_status === 'open'">
                                {{ printString('open') }}
                            </p>
                        </div>
                        <!-- <div class="program-block__label employee_dependents" v-if="scholarship.employee_dependents">
                            <p>{{ printString('employee_dependents') }}</p>
                        </div> -->
                    </div>

                    <div v-if="scholarship.employee_dependents" class="single-scholarship__sidebar__item">
                        <h3 class="step--1 black-primary">
                            {{ printString('employee_dependents') }}
                        </h3>
                        <p class="paragraph--teaser" v-if="scholarship.employee_dependents === '1'">
                            {{ printString('yes') }}
                        </p>
                        <p class="paragraph--teaser" v-else-if="scholarship.employee_dependents === '0'">
                            {{ printString('no') }}
                        </p>
                    </div>
                    <div v-if="scholarship.announcement_date" class="single-scholarship__sidebar__item">
                        <h3 class="step--1 black-primary">
                            {{ printString('announcement_date') }}
                        </h3>
                        <p class="paragraph--teaser">
                            {{ formatDate(scholarship.announcement_date) }}
                        </p>
                    </div>
                    <div v-if="scholarship.is_merit_based != 0 || scholarship.is_need_based != 0 || scholarship.is_contest != 0" class="single-scholarship__sidebar__item">
                        <h3 class="step--1 black-primary">
                            {{ printString('scholarship_type') }}
                        </h3>
                        <p class="paragraph--teaser" v-if="scholarship.is_merit_based === '1'">
                            {{ printString('merit_based') }}
                        </p>
                        <p class="paragraph--teaser" v-if="scholarship.is_need_based === '1'">
                            {{ printString('need_based') }}
                        </p>
                        <p class="paragraph--teaser" v-if="scholarship.is_contest === '1'">
                            {{ printString('contest') }}
                        </p>
                    </div>
                    <div v-if="scholarship.scholarship_focus" class="single-scholarship__sidebar__item">
                        <h3 class="step--1 black-primary">
                            {{ printString('area_of_focus') }}
                        </h3>
                        <p class="paragraph--teaser">
                            {{ scholarship.scholarship_focus }}
                        </p>
                    </div>
                    <div v-if="scholarship.degree_seeking && scholarship.degree_seeking.length" class="single-scholarship__sidebar__item">
                        <h3 class="step--1 black-primary">
                            {{ printString('degree_seeking') }}
                        </h3>
                        <p class="paragraph--teaser">
                            {{ formatArray(scholarship.degree_seeking) }}
                        </p>
                    </div>
                    <div v-if="scholarship.award_amount" class="single-scholarship__sidebar__item amount">
                        <h3 class="step--1 black-primary">
                            {{ printString('award_amount') }}
                        </h3>
                        <p class="paragraph--teaser step--5">
                            {{ formatAmount(scholarship.award_amount) }}
                        </p>
                    </div>
                    <div v-if="scholarship.application_url" class="single-scholarship__sidebar__item">
                        <a :href="scholarship.application_url" target="_blank" rel="noopener noreferrer" class="general-button orange-background">
                            {{ printString('apply_now') }}
                            <span v-html="ico_external_16"/>
                        </a>
                    </div>
                    <div v-if="scholarship.program_url" class="single-scholarship__sidebar__item">
                        <a :href="scholarship.program_url" target="_blank" rel="noopener noreferrer" class="general-button border-red">
                            {{ printString('scholarship_website') }}
                            <span v-html="ico_external_16"/>
                        </a>
                    </div>
                </div>

                <div class="single-scholarship__content">
                    <div class="single-scholarship__content__item single-scholarship__dates">
                        <div class="single-scholarship__dates__open" v-if="scholarship.open_date">
                            <span v-html="ico_success_icon"/>
                            <p class="step--1">
                                {{ printString('opening_date') }}
                            </p>
                            <p>{{ formatDate(scholarship.open_date) }}</p>
                        </div>
                        <div class="single-scholarship__dates__close" v-if="scholarship.close_date">
                            <span v-html="ico_calendar_deadline"/>
                            <p class="step--1">
                                {{ printString('closing_date') }}
                            </p>
                            <p>{{ formatDate(scholarship.close_date) }}</p>
                        </div>
                    </div>

                    <div class="single-scholarship__content__item">
                        <h2 class="step--4 black-primary margin--xs-s">
                            {{ printString('student_eligibility') }}
                        </h2>
                        <p class="margin--l-xl">
                            {{ printString('find_out_if_you_qualify_for_this_scholarship') }}
                        </p>

                        <div class="grid two">
                            <div v-if="scholarship.enrollment_status">
                                <h3 class="step--1 black-primary">
                                    {{ printString('enrollment_status') }}
                                </h3>
                                <p>{{ formatEnrollmentStatus(scholarship.enrollment_status) }}</p>
                            </div>
                            <div v-if="scholarship.citizenship_status && scholarship.citizenship_status.length">
                                <h3 class="step--1 black-primary">
                                    {{ printString('citizenship_status') }}
                                </h3>
                                <p>{{ formatArray(scholarship.citizenship_status) }}</p>
                            </div>
                            <div v-if="formatArray(scholarship.current_grade_level)">
                                <h3 class="step--1 black-primary">
                                    {{ printString('current_grade_level') }}
                                </h3>
                                <p>{{ formatArray(scholarship.current_grade_level) }}</p>
                            </div>
                            <div v-if="formatArray(scholarship.graduation_status)">
                                <h3 class="step--1 black-primary">
                                    {{ printString('graduation_status') }}
                                </h3>
                                <p>{{ formatArray(scholarship.graduation_status) }}</p>
                            </div>
                            <div v-if="formatArray(scholarship.country)">
                                <h3 class="step--1 black-primary">
                                    {{ printString('current_country') }}
                                </h3>
                                <p class="capitalize">{{ formatArray(scholarship.country) }}</p>
                            </div>
                            <div v-if="formatArray(scholarship.provinces)">
                                <h3 class="step--1 black-primary">
                                    {{ printString('province_or_territory') }}
                                </h3>
                                <p>{{ formatArray(scholarship.provinces) }}</p>
                            </div>
                        </div>
                    </div>

                    <div v-if="scholarship.description" class="single-scholarship__content__item">
                        <h2 class="step--4 black-primary">
                            {{ printString('scholarship_description') }}
                        </h2>
                        <div class="content paragraph--regular" v-html="scholarship.description"></div>
                    </div>
                    <div class="single-scholarship__content__item" v-if="scholarship.min_age || scholarship.max_age || scholarship.award_duration || scholarship.renewable || scholarship.restrictions">
                        <div v-if="scholarship.min_age" class="single-scholarship__content__item__row">
                            <p class="step--1 black-primary">
                                {{ printString('minimum_age') }}
                            </p>
                            <p>{{ scholarship.min_age }}</p>
                        </div>
                        <div v-if="scholarship.max_age" class="single-scholarship__content__item__row">
                            <p class="step--1 black-primary">
                                {{ printString('maximum_age') }}
                            </p>
                            <p>{{ scholarship.max_age }}</p>
                        </div>
                        <div v-if="scholarship.award_duration" class="single-scholarship__content__item__row">
                            <p class="step--1 black-primary">
                                {{ printString('award_duration') }}
                            </p>
                            <p>{{ scholarship.award_duration }}</p>
                        </div>
                        <div v-if="scholarship.renewable" class="single-scholarship__content__item__row">
                            <p class="step--1 black-primary">
                                {{ printString('duration') }}
                            </p>
                            <p>{{ scholarship.renewable }}</p>
                        </div>
                        <div v-if="scholarship.restrictions" class="single-scholarship__content__item__row">
                            <p class="step--1 black-primary">
                                {{ printString('restrictions') }}
                            </p>
                            <p>{{ formatArray(scholarship.restrictions) }}</p>
                        </div>
                    </div>
                    <div class="single-scholarship__content__item">
                        <p class="paragraph--teaser color--grey">
                            {{ printString('disclaimer_scholarship_single') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRoute, useRouter } from 'vue-router';
import { Menu } from 'floating-vue'
import LoadingIntro from './LoadingIntro.vue';

export default {
    name: "SingleScholarship",
    components: {
        Menu,
        LoadingIntro,
    },
    data() {
        return {
            site_lang: window.lang,
            scholarship: null,
            loading: false,
            error: null,
            savedList: [],
            scholarship_status: '',
            ico_close_16: window.ico_close_16,
            ico_favorite_16: window.ico_favorite_16,
            ico_favorite_16_checked: window.ico_favorite_16_checked,
            ico_success_icon: window.ico_success_icon,
            ico_calendar_deadline: window.ico_calendar_deadline,
            ico_arrow_left_16: window.ico_arrow_left_16,
            ico_external_16: window.ico_external_16,
            is_logged_in: window.is_logged_in,
            saveButtonText: this.printString('save'),
            maxSavedItems: 10,
            loginUrl: window.log_in_url,
            tooltipTheme: 'tooltips',
            menuContainer: null,
            isSaveFetching: false,
            lastPath: 'scholarships',
            // tempCurrentDate: new Date('2024-01-16'), // Commented out test date
        };
    },
    created() {
        this.route = useRoute();
        this.router = useRouter();
                // Get the initial previous route from the navigation state if available
        if (this.router.options.history.state?.back) {
            this.previousRoute = this.router.options.history.state.back;
            // Set lastPath based on the previous route
            if (this.previousRoute.includes('/scholarships')) {
                this.lastPath = 'scholarships';
            } else if (this.previousRoute.includes('/my-dashboard')) {
                this.lastPath = 'my-dashboard';
            }
        }
    },
    computed: {
        isSaved() {
            if (!this.scholarship) return false;
            const isInList = this.savedList.some(pair => 
                pair[0] === this.scholarship.scholarship_id || 
                pair[1] === this.scholarship.scholarship_id
            );
            this.saveButtonText = isInList ? this.printString('remove') : this.printString('save');
            return isInList;
        },
        isSavedListFull() {
            return this.savedList.length >= this.maxSavedItems;
        },
        labelText() {
            // Use actual current date for Ontario (Eastern Time)
            const currentDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' }));
            const openDate = this.parseDate(this.scholarship.open_date);
            const closeDate = this.parseDate(this.scholarship.close_date);

            if (openDate && currentDate < openDate && this.isWithinDays(currentDate, openDate, 30)) {
                return this.scholarship_status = 'opening';
            } else if (closeDate && currentDate < closeDate) {
                if (this.isWithinDays(currentDate, closeDate, 14)) {
                    return this.scholarship_status = 'closing';
                } else if (openDate && currentDate >= openDate) {
                    return this.scholarship_status = 'open';
                }
            }
            return null;
        },
        tooltipOptions() {
            if (this.is_logged_in) {
                return {
                    content: this.saveButtonText,
                    theme: 'tooltips',
                };
            } else {
                return {
                    content: `<div><a href="${this.loginUrl}">${this.printString('sign_in')}</a> ${this.printString('to_add_to_list')}</div>`,
                    html: true,
                    theme: 'tooltips',
                    triggers: ['hover', 'focus', 'touch'],
                    hideOnClick: false,
                };
            }
        },
        // alternateLanguageUrl() {
        //     const baseUrl = this.lang === 'en' ? '/fr/scholarship/' : '/scholarship/';
        //     return `${baseUrl}${this.scholarship?.alt_lang_id}`;
        // }
    },
    methods: {
        async getCacheKey(requestData) {
            const sortedData = Object.keys(requestData).sort().reduce((obj, key) => {
                obj[key] = requestData[key];
                return obj;
            }, {});
            return `scholarship-single-${JSON.stringify(sortedData)}`;
        },

        async getCachedData(cacheKey) {
            try {
                const cache = await caches.open('scholarship-single-cache');
                const cachedResponse = await cache.match(cacheKey);
                
                if (cachedResponse) {
                    const cacheData = await cachedResponse.json();
                    const ageInMinutes = (Date.now() - cacheData.timestamp) / (1000 * 60);
                    
                    if (ageInMinutes > cacheData.expirationMinutes) {
                        console.log('Cache expired, removing old data');
                        await cache.delete(cacheKey);
                        return null;
                    }
                    
                    return cacheData.data;
                }
            } catch (error) {
                console.warn('Error reading from cache:', error);
            }
            return null;
        },

        async setCachedData(cacheKey, data, expirationMinutes = 300) { // 30 minutes default cache
            try {
                const cache = await caches.open('scholarship-single-cache');
                const cacheData = {
                    data: data,
                    timestamp: Date.now(),
                    expirationMinutes: expirationMinutes
                };
                const response = new Response(JSON.stringify(cacheData));
                await cache.put(cacheKey, response);
            } catch (error) {
                console.warn('Error writing to cache:', error);
            }
        },

        async fetchScholarship(apikey, site_lang, scholarship_id) {
            this.loading = true;
            this.error = null;

            try {
                const requestData = {
                    apikey: apikey,
                    site_lang: site_lang,
                    scholarship_id: scholarship_id,
                };

                // Try to get cached data first
                const cacheKey = await this.getCacheKey(requestData);
                const cachedData = await this.getCachedData(cacheKey);

                if (cachedData) {
                    console.log('Using cached scholarship data');
                    this.handleScholarshipData(cachedData);
                    return;
                }

                const response = await fetch(`${window.api_base_url}/api/scholarships-single/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestData),
                });

                if (!response.ok) {
                    throw new Error(`Error: ${response.statusText}`);
                }

                const data = await response.json();
                
                // Determine cache duration based on scholarship status
                let cacheDuration = 30; // default 30 minutes
                if (data.scholarship && data.scholarship[0]) {
                    const scholarship = data.scholarship[0];
                    const currentDate = new Date();
                    const closeDate = this.parseDate(scholarship.close_date);
                    
                    if (closeDate) {
                        // If closing date is within 24 hours, cache for shorter time
                        const hoursUntilClose = (closeDate - currentDate) / (1000 * 60 * 60);
                        if (hoursUntilClose <= 24) {
                            cacheDuration = 5; // 5 minutes cache for soon-closing scholarships
                        }
                    }
                }
                
                // Cache the response
                await this.setCachedData(cacheKey, data, cacheDuration);
                
                // Handle the data
                this.handleScholarshipData(data);

            } catch (err) {
                this.error = "Failed to load scholarship. Please try again.";
                console.error("Error fetching scholarship:", err);
            } finally {
                this.loading = false;
            }
        },

        handleScholarshipData(data) {
            if (data.scholarship && data.scholarship.length > 0) {
                console.log(data.scholarship[0]);
                this.scholarship = data.scholarship[0];
            } else {
                console.error("No scholarship data received");
                this.error = "No scholarship data found";
            }

            this.$nextTick(() => {
                this.updateLanguageSwitcherUrl();
            });
        },

        async clearScholarshipCache() {
            try {
                const cache = await caches.open('scholarship-single-cache');
                await cache.keys().then(keys => {
                    keys.forEach(key => {
                        cache.delete(key);
                    });
                });
                console.log('Scholarship cache cleared');
            } catch (error) {
                console.warn('Error clearing cache:', error);
            }
        },

        async clearSpecificScholarshipCache(scholarshipId) {
            try {
                const cache = await caches.open('scholarship-single-cache');
                const keys = await cache.keys();
                
                for (const key of keys) {
                    if (key.url.includes(scholarshipId)) {
                        await cache.delete(key);
                        console.log(`Cleared cache for scholarship ${scholarshipId}`);
                    }
                }
            } catch (error) {
                console.warn('Error clearing specific scholarship cache:', error);
            }
        },

        goBackToScholarships() {
            // Use the stored previous route if it exists and is a valid scholarships page
            if (this.previousRoute && this.previousRoute.includes('/scholarships')) {
                // const route = this.site_lang === 'fr' ? '/fr/scholarships' : '/scholarships';
                const route = this.site_lang === 'fr' ? '/scholarships' : '/scholarships';
                this.router.push({ path: route });
            } else if(this.previousRoute && this.previousRoute.includes('/my-dashboard')) {
                // const route = this.site_lang === 'fr' ? '/fr/my-dashboard' : '/my-dashboard';
                const route = this.site_lang === 'fr' ? '/my-dashboard' : '/my-dashboard';
                this.router.push({ path: route });
            } else {
                // Fallback to default behavior
                // const route = this.site_lang === 'fr' ? '/fr/scholarships' : '/scholarships';
                const route = this.site_lang === 'fr' ? '/scholarships' : '/scholarships';
                this.router.push({ path: route });
            }
        },
        handleSaveClick(event) {
            event.stopPropagation();
            if (!this.is_logged_in) {
                window.location.href = this.loginUrl;
                return;
            } else {
                this.saveToList();
            }
        },
        async saveToList() {
            if (this.scholarship) {
                const scholarshipPair = [
                    this.scholarship.language_code === 'en' ? this.scholarship.scholarship_id : (this.scholarship.alt_lang_id || ''),
                    this.scholarship.language_code === 'fr' ? this.scholarship.scholarship_id : (this.scholarship.alt_lang_id || '')
                ];
                
                const index = this.savedList.findIndex(pair => 
                    pair[0] === scholarshipPair[0] || pair[1] === scholarshipPair[1]
                );

                if (index !== -1) {
                    this.savedList.splice(index, 1);
                    this.saveButtonText = this.printString('save');
                } else if (this.savedList.length < this.maxSavedItems) {
                    this.savedList.push(scholarshipPair);
                    this.saveButtonText = this.printString('remove');
                } else {
                    console.warn(`Saved list is full. Max ${this.maxSavedItems} scholarships allowed.`);
                    return;
                }

                console.log('Updated Saved list:', this.savedList);

                try {
                    const response = await fetch(`${window.rest_api}/wp-json/custom/v1/save-scholarships`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-WP-Nonce': window.nonce,
                        },
                        credentials: 'same-origin',
                        body: JSON.stringify({ savedList: this.savedList }),
                    });

                    if (!response.ok) {
                        const errorBody = await response.text();
                        console.error(`Error saving scholarships: ${response.status} - ${response.statusText}`);
                        console.error(`Response body: ${errorBody}`);
                    } else {
                        console.log('Scholarships saved to ACF successfully.');
                    }
                } catch (error) {
                    console.error('Error sending saved list to backend:', error);
                }
            }
        },
        async fetchSavedScholarships() {
            try {
                const response = await fetch(`${window.rest_api}/wp-json/custom/v1/get-saved-scholarships`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.nonce,
                    },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        console.warn('User is not logged in. Cannot fetch saved scholarships.');
                        this.savedList = [];
                    } else {
                        console.error(`Error fetching saved scholarships: ${response.status}`);
                    }
                    return;
                }

                const savedScholarships = await response.json();
                console.log('Fetched saved scholarships:', savedScholarships);

                if (Array.isArray(savedScholarships) && savedScholarships.length > 0) {
                    // Ensure all items are arrays
                    this.savedList = savedScholarships.map(item => 
                        Array.isArray(item) ? item : [item, '']
                    );
                } else {
                    this.savedList = [];
                }
            } catch (error) {
                console.error("Error fetching saved scholarships:", error);
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        },
        formatEnrollmentStatus(status) {
            const statusMap = {
                ft_pt: this.printString('ft_pt'),
                ft: this.printString('ft'),
                pt: this.printString('pt')
            };
            return statusMap[status] || status;
        },
        formatArray(arrayInput) {
            if (!arrayInput) return '';
            
            // Handle empty PHP serialized array
            if (arrayInput === 'a:0:{}') return '';
            
            // Handle already unserialized array
            if (Array.isArray(arrayInput)) {
                return arrayInput
                    .filter(value => value !== null) // Filter out null values
                    .map(value => {
                        // Map province codes to full names
                        const provinceMap = {
                            'AB': this.printString('province_alberta'),
                            'BC': this.printString('province_british_columbia'),
                            'MB': this.printString('province_manitoba'),
                            'NB': this.printString('province_new_brunswick'),
                            'NL': this.printString('province_newfoundland_and_labrador'),
                            'NS': this.printString('province_nova_scotia'),
                            'NT': this.printString('province_northwest_territories'),
                            'NU': this.printString('province_nunavut'),
                            'ON': this.printString('province_ontario'),
                            'PE': this.printString('province_prince_edward_island'),
                            'QC': this.printString('province_quebec'),
                            'SK': this.printString('province_saskatchewan'),
                            'YT': this.printString('province_yukon'),
                            'recommendation_only': this.printString('recommendation_only')
                        };
                        
                        return provinceMap[value] || (value ? value.replace(/_/g, ' ') : '');
                    })
                    .filter(value => value) // Remove any empty strings
                    .join(', ');
            }
            
            // Handle serialized array string
            if (typeof arrayInput === 'string' && arrayInput.startsWith('a:')) {
                const matches = arrayInput.match(/s:\d+:"([^"]*)";/g);
                if (matches) {
                    return matches
                        .map(match => {
                            const value = match.replace(/s:\d+:"(.*?)";/, '$1');
                            const provinceMap = {
                                'AB': this.printString('province_alberta'),
                                'BC': this.printString('province_british_columbia'),
                                'MB': this.printString('province_manitoba'),
                                'NB': this.printString('province_new_brunswick'),
                                'NL': this.printString('province_newfoundland_and_labrador'),
                                'NS': this.printString('province_nova_scotia'),
                                'NT': this.printString('province_northwest_territories'),
                                'NU': this.printString('province_nunavut'),
                                'ON': this.printString('province_ontario'),
                                'PE': this.printString('province_prince_edward_island'),
                                'QC': this.printString('province_quebec'),
                                'SK': this.printString('province_saskatchewan'),
                                'YT': this.printString('province_yukon'),
                                'recommendation_only': this.printString('recommendation_only')
                            };
                            
                            return provinceMap[value] || value.replace(/_/g, ' ');
                        })
                        .join(', ');
                }
            }
            
            // Fallback for comma-separated strings
            return String(arrayInput).split(',').map(item => item.trim().replace(/_/g, ' ')).join(', ');
        },
        parseDate(dateString) {
            if (!dateString) return null;

            // Check if the date is in YYYY-MM-DD format
            if (dateString.includes('-')) {
                return new Date(dateString);
            }
            
            // If not, assume it's in YYYYMMDD format
            const year = dateString.substring(0, 4);
            const month = dateString.substring(4, 6);
            const day = dateString.substring(6, 8);
            return new Date(year, month - 1, day);
        },
        isWithinDays(date1, date2, days) {
            const diffTime = Math.abs(date2 - date1);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays <= days;
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = this.parseDate(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit' 
            }).replace(/(\d+)\/(\d+)\/(\d+)/, '$1/$2/$3');
        },
        formatAmount(amount) {
            if (!amount) return '';
            
            // Check if the amount is a number or contains only currency/numbers
            const isSimpleAmount = /^[$€£¥]?\d+([,.]\d+)?$/.test(amount.toString().trim());
            
            if (isSimpleAmount) {
                // Handle simple numeric amounts
                const hasCurrency = /[$€£¥]/.test(amount.toString());
                const cleanAmount = amount.toString().replace(/[$€£¥,]/g, '');
                const formattedAmount = cleanAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                return hasCurrency ? formattedAmount : `$${formattedAmount}`;
            } else {
                // Handle complex text descriptions with amounts
                return amount.toString().replace(/\$(\d+)/g, (match, num) => {
                    const formattedNum = num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    return `$${formattedNum}`;
                });
            }
        },
        updateLanguageSwitcherUrl() {
            if (this.scholarship) {
                const languageSwitcher = document.querySelector('.languages');
                if (languageSwitcher) {
                    const currentLang = this.site_lang;
                    // console.log(currentLang);
                    const otherLang = currentLang === 'en' ? 'fr' : '';
                    const langLink = languageSwitcher.querySelector(`.languages__item[href*="/${otherLang}/"]`);
                    
                    if (langLink) {
                        langLink.href = this.alternateLanguageUrl;
                    }
                }
            }
        },
        updateLanguageSwitcherUrl() {
            if (this.scholarship) {
                const languageSwitcher = document.querySelector('.languages');
                if (languageSwitcher) {
                    const langLink = languageSwitcher.querySelector(`.languages__item:not(.active)`);
                    
                    if (langLink) {
                        const scholarshipId = this.scholarship.alt_lang_id;
                        let baseUrl = window.language_switcher_url;
                        let newPath;

                        if (this.site_lang === 'fr') {
                            // We're on French site, building English URL
                            newPath = `/scholarship/${scholarshipId}`;
                            // Remove '/fr' from the end of baseUrl if it exists
                            baseUrl = baseUrl.replace(/\/fr\//, '');
                        } else {
                            // We're on English site, building French URL
                            newPath = `/scholarship/${scholarshipId}`;
                        }
                        
                        const newUrl = `${baseUrl}${newPath}`;
                        langLink.href = newUrl;
                    }
                }
            }
        },
        setMenuContainer() {
            this.$nextTick(() => {
                this.menuContainer = this.$el.querySelector('.single-scholarship__intro__buttons');
            });
        },
        printString(string) {
            return window.translations[string];
        },
    },
    mounted() {
        const apikey = window.api_key;
        const site_lang = this.site_lang;
        this.fetchScholarship(apikey, site_lang, this.$route.params.id);
        if (window.is_logged_in) {
            this.fetchSavedScholarships();
        }
        this.setMenuContainer();

        this.$watch(
            () => this.scholarship,
            (newScholarship) => {
                if (newScholarship) {
                    this.$nextTick(() => {
                        this.updateLanguageSwitcherUrl();
                    });
                }
            },
            { immediate: true }
        );
    },
    watch: {
        '$route.params.id'(newId) {
            const apikey = window.api_key;
            const site_lang = this.site_lang;
            this.fetchScholarship(apikey, site_lang, newId);
            this.$nextTick(() => {
                this.setMenuContainer();
            });
        },
        scholarship() {
            this.$nextTick(() => {
                this.setMenuContainer();
            });
        },
    },
};
</script>






